import Swal from "sweetalert2";
import {
    createMessage,
    getAllMessages,
    getMessageById,
    updateMessage,
    deleteMessage,
    showMessage,
    hideMessage,
    getActiveMessagesByType,
    getAllActiveMessages
} from '../../services/ApplicationMessagesService';

// Action Types
export const LOAD_MESSAGES_REQUEST = '[APPLICATION_MESSAGES] LOAD_MESSAGES_REQUEST';
export const LOAD_MESSAGES_SUCCESS = '[APPLICATION_MESSAGES] LOAD_MESSAGES_SUCCESS';
export const LOAD_MESSAGES_FAILED = '[APPLICATION_MESSAGES] LOAD_MESSAGES_FAILED';

export const LOAD_MESSAGE_BY_ID_REQUEST = '[APPLICATION_MESSAGES] LOAD_MESSAGE_BY_ID_REQUEST';
export const LOAD_MESSAGE_BY_ID_SUCCESS = '[APPLICATION_MESSAGES] LOAD_MESSAGE_BY_ID_SUCCESS';
export const LOAD_MESSAGE_BY_ID_FAILED = '[APPLICATION_MESSAGES] LOAD_MESSAGE_BY_ID_FAILED';

export const SAVE_MESSAGE_REQUEST = '[APPLICATION_MESSAGES] SAVE_MESSAGE_REQUEST';
export const SAVE_MESSAGE_SUCCESS = '[APPLICATION_MESSAGES] SAVE_MESSAGE_SUCCESS';
export const SAVE_MESSAGE_FAILED = '[APPLICATION_MESSAGES] SAVE_MESSAGE_FAILED';

export const DELETE_MESSAGE_REQUEST = '[APPLICATION_MESSAGES] DELETE_MESSAGE_REQUEST';
export const DELETE_MESSAGE_SUCCESS = '[APPLICATION_MESSAGES] DELETE_MESSAGE_SUCCESS';
export const DELETE_MESSAGE_FAILED = '[APPLICATION_MESSAGES] DELETE_MESSAGE_FAILED';

export const TOGGLE_MESSAGE_REQUEST = '[APPLICATION_MESSAGES] TOGGLE_MESSAGE_REQUEST';
export const TOGGLE_MESSAGE_SUCCESS = '[APPLICATION_MESSAGES] TOGGLE_MESSAGE_SUCCESS';
export const TOGGLE_MESSAGE_FAILED = '[APPLICATION_MESSAGES] TOGGLE_MESSAGE_FAILED';

export const LOAD_ACTIVE_MESSAGES_REQUEST = '[APPLICATION_MESSAGES] LOAD_ACTIVE_MESSAGES_REQUEST';
export const LOAD_ACTIVE_MESSAGES_SUCCESS = '[APPLICATION_MESSAGES] LOAD_ACTIVE_MESSAGES_SUCCESS';
export const LOAD_ACTIVE_MESSAGES_FAILED = '[APPLICATION_MESSAGES] LOAD_ACTIVE_MESSAGES_FAILED';

// Load all messages with filters
export const loadMessages = (params = {}) => {
    return (dispatch) => {
        dispatch({ type: LOAD_MESSAGES_REQUEST });
        return getAllMessages(params)
            .then((response) => {
                dispatch({
                    type: LOAD_MESSAGES_SUCCESS,
                    payload: {
                        data: response.data.data,
                        pagination: {
                            total: response.data.total || response.data.data.length,
                            totalPages: response.data.totalPages || 1,
                            currentPage: response.data.currentPage || 1,
                        }
                    }
                });
            })
            .catch((error) => {
                dispatch({ type: LOAD_MESSAGES_FAILED });
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: error.response?.data?.message || 'Failed to load messages'
                });
            });
    };
};

// Load message by ID
export const loadMessageById = (id) => {
    return (dispatch) => {
        dispatch({ type: LOAD_MESSAGE_BY_ID_REQUEST });
        return getMessageById(id)
            .then((response) => {
                dispatch({
                    type: LOAD_MESSAGE_BY_ID_SUCCESS,
                    payload: response.data.data
                });
            })
            .catch((error) => {
                dispatch({ type: LOAD_MESSAGE_BY_ID_FAILED });
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: error.response?.data?.message || 'Failed to load message'
                });
            });
    };
};

// Save message (create or update)
export const saveMessageAction = (values, isEditMode, id, navigate) => {
    return (dispatch) => {
        dispatch({ type: SAVE_MESSAGE_REQUEST });
        
        const apiCall = isEditMode ? updateMessage(id, values) : createMessage(values);
        
        return apiCall
            .then((response) => {
                dispatch({ type: SAVE_MESSAGE_SUCCESS });
                Swal.fire({
                    icon: 'success',
                    title: 'Success',
                    text: response.data.message || `Message ${isEditMode ? 'updated' : 'created'} successfully`
                }).then(() => {
                    navigate('/application-messages');
                });
            })
            .catch((error) => {
                dispatch({ type: SAVE_MESSAGE_FAILED });
                const errors = error.response?.data?.errors;
                let errorMessage = error.response?.data?.message || 'Failed to save message';
                
                if (errors && Array.isArray(errors)) {
                    errorMessage = errors.map(err => err.msg).join(', ');
                }
                
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: errorMessage
                });
            });
    };
};

// Delete message
export const deleteMessageAction = (id) => {
    return (dispatch) => {
        return Swal.fire({
            title: 'Are you sure?',
            text: 'Do you want to delete this message?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, delete it!'
        }).then((result) => {
            if (result.isConfirmed) {
                dispatch({ type: DELETE_MESSAGE_REQUEST });
                return deleteMessage(id)
                    .then((response) => {
                        dispatch({ type: DELETE_MESSAGE_SUCCESS });
                        Swal.fire({
                            icon: 'success',
                            title: 'Deleted!',
                            text: 'Message has been deleted.'
                        });
                        dispatch(loadMessages());
                    })
                    .catch((error) => {
                        dispatch({ type: DELETE_MESSAGE_FAILED });
                        Swal.fire({
                            icon: 'error',
                            title: 'Error',
                            text: error.response?.data?.message || 'Failed to delete message'
                        });
                    });
            }
        });
    };
};

// Toggle message visibility (show/hide)
export const toggleMessageAction = (id, isActive) => {
    return (dispatch) => {
        dispatch({ type: TOGGLE_MESSAGE_REQUEST });
        
        const apiCall = isActive ? hideMessage(id) : showMessage(id);
        
        return apiCall
            .then((response) => {
                dispatch({ type: TOGGLE_MESSAGE_SUCCESS });
                Swal.fire({
                    icon: 'success',
                    title: 'Success',
                    text: response.data.message,
                    timer: 2000
                });
                dispatch(loadMessages());
            })
            .catch((error) => {
                dispatch({ type: TOGGLE_MESSAGE_FAILED });
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: error.response?.data?.message || 'Failed to toggle message'
                });
            });
    };
};

// Load active messages by type (public endpoint)
export const loadActiveMessagesByType = (messageType) => {
    return (dispatch) => {
        dispatch({ type: LOAD_ACTIVE_MESSAGES_REQUEST });
        return getActiveMessagesByType(messageType)
            .then((response) => {
                dispatch({
                    type: LOAD_ACTIVE_MESSAGES_SUCCESS,
                    payload: response.data.data
                });
            })
            .catch((error) => {
                dispatch({ type: LOAD_ACTIVE_MESSAGES_FAILED });
                console.error('Failed to load active messages:', error);
            });
    };
};

// Load all active messages (public endpoint)
export const loadAllActiveMessages = () => {
    return (dispatch) => {
        dispatch({ type: LOAD_ACTIVE_MESSAGES_REQUEST });
        return getAllActiveMessages()
            .then((response) => {
                dispatch({
                    type: LOAD_ACTIVE_MESSAGES_SUCCESS,
                    payload: response.data.data
                });
                return response.data.data;
            })
            .catch((error) => {
                dispatch({ type: LOAD_ACTIVE_MESSAGES_FAILED });
                console.error('Failed to load active messages:', error);
            });
    };
};
